import { getDaysInMonth, isWeekend, format, addDays, getDay, subDays } from 'date-fns';

// ============================================================================
// KONFIGURATION & KONSTANTEN
// ============================================================================

const SHIFTS = ['früh', 'zwischen', 'spät'];
const DAYS_OF_WEEK = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const SCORING = {
  PENALTY: {
    FREE_WISH: 20,
    SHIFT_MISMATCH: 8,
    CRITICAL_THRESHOLD: 25,
    WEEKEND_MULTIPLIER: 1.3,
    HOLIDAY_MULTIPLIER: 1.2
  },
  BONUS: {
    MATCH: 1, // Basis-Multiplikator für Severity
    WEEKEND_MULTIPLIER: 1.5,
    HOLIDAY_MULTIPLIER: 1.3
  }
};

// ============================================================================
// HILFSFUNKTIONEN (PURE)
// ============================================================================

// Check if the date is a holiday
function isHoliday(date, holidays) {
  if (!holidays || !holidays.length) return false;
  const dateStr = format(date, 'yyyy-MM-dd');
  return holidays.some(holiday => holiday.date === dateStr);
}

// Erstellt eine Map für schnellen Datums-Lookup (Vermeidet parseISO/format in Schleifen)
function createDateLookupMap(year, month) {
  const map = {};
  const startDate = new Date(year, month - 1, 1);
  // Wir schauen auch in den Vormonat für Übergänge
  const lookBackStart = subDays(startDate, 7); 
  const endDate = new Date(year, month, 0); // Letzter Tag des Monats
  
  let current = lookBackStart;
  while (current <= endDate) {
    const currentStr = format(current, 'yyyy-MM-dd');
    const prevDate = addDays(current, -1);
    const prevStr = format(prevDate, 'yyyy-MM-dd');
    
    map[currentStr] = {
      prev: prevStr,
      dateObj: current,
      isWeekend: isWeekend(current),
      dayOfWeek: getDay(current)
    };
    current = addDays(current, 1);
  }
  return map;
}

function getWorkingDaysInMonth(year, month) {
  const startDate = new Date(year, month - 1, 1);
  const endDate = new Date(year, month, 0);
  let workingDays = 0;
  let currentDate = new Date(startDate);
  
  while (currentDate <= endDate) {
    const day = currentDate.getDay();
    if (day !== 0 && day !== 6) workingDays++;
    currentDate.setDate(currentDate.getDate() + 1);
  }
  return workingDays;
}

// Optimized: Nutzt die Date-Map für O(1) Lookups statt Datums-Mathematik
function calculateConsecutiveDays(schedule, employeeId, dateStr, dateMap) {
  let consecutiveDays = 0;
  let checkDateStr = dateMap[dateStr]?.prev;
  
  // Safety break nach 14 Tagen (optimistisch)
  for (let i = 0; i < 14; i++) {
    if (!checkDateStr) break; // Ende des Kalenders/Maps erreicht
    
    const daySchedule = schedule[checkDateStr];
    if (!daySchedule) break; // Keine Daten für diesen Tag (z.B. vor Planbeginn)
    
    // Prüfen ob Mitarbeiter in irgendeiner Schicht war
    const isWorking = SHIFTS.some(s => daySchedule[s] && daySchedule[s].includes(employeeId));
    
    if (!isWorking) break;
    
    consecutiveDays++;
    checkDateStr = dateMap[checkDateStr]?.prev;
  }
  
  return consecutiveDays;
}

function calculateDaysOffBetweenBlocks(schedule, employeeId, dateStr, dateMap) {
  let daysOff = 0;
  let checkDateStr = dateMap[dateStr]?.prev;
  
  for (let i = 0; i < 14; i++) {
    if (!checkDateStr) break;
    
    const daySchedule = schedule[checkDateStr];
    if (!daySchedule) break;

    const isWorking = SHIFTS.some(s => daySchedule[s] && daySchedule[s].includes(employeeId));
    
    if (isWorking) break; // Arbeitstag gefunden -> Zählung beenden
    
    daysOff++;
    checkDateStr = dateMap[checkDateStr]?.prev;
  }
  
  return daysOff;
}

function workedLateShiftYesterday(schedule, employeeId, dateStr, dateMap) {
  const yesterdayStr = dateMap[dateStr]?.prev;
  if (!yesterdayStr) return false;
  
  const daySchedule = schedule[yesterdayStr];
  return daySchedule && daySchedule.spät && daySchedule.spät.includes(employeeId);
}

function getWeekendPairs(allDays) {
  const weekendPairs = [];
  let currentPair = [];
  
  for (const date of allDays) {
    if (isWeekend(date)) {
      currentPair.push(date);
      if (currentPair.length === 2) {
        weekendPairs.push([...currentPair]);
        currentPair = [];
      }
    }
  }
  if (currentPair.length === 1) weekendPairs.push([...currentPair]);
  return weekendPairs;
}

function getIdealShiftDistribution(employee) {
  const skills = employee.skills || [];
  if (skills.length === 0) return {};
  const share = 1 / skills.length;
  return SHIFTS.reduce((acc, shift) => {
    acc[shift] = skills.includes(shift) ? share : 0;
    return acc;
  }, {});
}

function calculateShiftImbalance(stats, idealDistribution) {
  if (!stats || stats.totalDays === 0) return 0;
  return SHIFTS.reduce((sum, shift) => {
    const actual = stats[shift] / stats.totalDays;
    const ideal = idealDistribution[shift] || 0;
    return sum + Math.abs(actual - ideal);
  }, 0);
}

// ============================================================================
// PREFERENCE & SCORING LOGIC
// ============================================================================

function checkEmployeePreference(employee, dateStr, shift) {
  if (!employee.preferences?.length) {
    return { hasPreference: false, matches: true, type: 'none', severity: 0 };
  }
  
  // Optimierung: Map verwenden wenn viele Preferences existieren, hier Array.find ok
  const preference = employee.preferences.find(p => p.date === dateStr);
  
  if (!preference) return { hasPreference: false, matches: true, type: 'none', severity: 0 };

  if (preference.type === 'frei') {
    return { 
      hasPreference: true, 
      matches: false, 
      type: 'frei', 
      severity: 15, 
      reason: preference.reason 
    };
  }
  
  if (preference.type === shift) {
    return { 
      hasPreference: true, 
      matches: true, 
      type: shift, 
      severity: -8, 
      reason: preference.reason 
    };
  }
  
  return { 
    hasPreference: true, 
    matches: false, 
    type: preference.type, 
    severity: 10, 
    reason: preference.reason 
  };
}

function calculateScoreComponents(employee, dateStr, shift, context = 'normal') {
  const prefCheck = checkEmployeePreference(employee, dateStr, shift);
  let penalty = Math.max(0, prefCheck.severity);
  let bonus = 0;

  // Context Multipliers
  const isWeekend = context === 'weekend';
  const isHoliday = context === 'holiday';
  const pMult = isWeekend ? SCORING.PENALTY.WEEKEND_MULTIPLIER : (isHoliday ? SCORING.PENALTY.HOLIDAY_MULTIPLIER : 1.0);
  const bMult = isWeekend ? SCORING.BONUS.WEEKEND_MULTIPLIER : (isHoliday ? SCORING.BONUS.HOLIDAY_MULTIPLIER : 1.0);

  if (prefCheck.hasPreference) {
    if (!prefCheck.matches) {
      if (prefCheck.type === 'frei') {
        penalty += SCORING.PENALTY.FREE_WISH;
        if (isWeekend || isHoliday) penalty += 10;
      } else {
        penalty += SCORING.PENALTY.SHIFT_MISMATCH;
      }
    } else if (prefCheck.severity < 0) {
      bonus = Math.abs(prefCheck.severity);
    }
  }

  return {
    penalty: Math.round(penalty * pMult),
    bonus: Math.round(bonus * bMult),
    prefCheck
  };
}

// ============================================================================
// MAIN GENERATOR
// ============================================================================

export async function generateSchedule(employees, monthStr, settings) {
  const [year, month] = monthStr.split('-').map(Number);
  const daysInMonth = getDaysInMonth(new Date(year, month - 1));
  const workingDaysInMonth = getWorkingDaysInMonth(year, month);
  
  // Date Lookup Map initialisieren
  const dateMap = createDateLookupMap(year, month);
  
  // Datenstrukturen
  const schedule = {};
  const violations = [];
  const daysWithoutZwischendienst = [];
  const explanations = [];
  
  // Tracking
  const tracker = {
    hours: {},
    stats: {},
    consecutive: {},
    lastShift: {},
    weekendShifts: {},
    dailyAssignments: {},
    idealDist: {},
    targetHours: {}
  };

  // Shift Durations berechnen
  const shiftDurations = {};
  let totalShiftDuration = 0;
  for (const shift of SHIFTS) {
    const dur = settings.shifts[shift].hours + (settings.shifts[shift].minutes / 60);
    shiftDurations[shift] = dur;
    totalShiftDuration += dur;
  }
  const avgShiftDuration = totalShiftDuration / SHIFTS.length;

  // Mitarbeiter initialisieren
  employees.forEach(emp => {
    tracker.hours[emp.id] = 0;
    tracker.stats[emp.id] = { früh: 0, zwischen: 0, spät: 0, totalDays: 0 };
    tracker.consecutive[emp.id] = 0;
    tracker.lastShift[emp.id] = null;
    tracker.weekendShifts[emp.id] = 0;
    tracker.dailyAssignments[emp.id] = {};
    tracker.idealDist[emp.id] = getIdealShiftDistribution(emp);
    tracker.targetHours[emp.id] = workingDaysInMonth * avgShiftDuration * (emp.workload / 100);
  });

  // Alle Tage als Array für Iteration
  const allDays = [];
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(year, month - 1, d);
    allDays.push(date);
    schedule[format(date, 'yyyy-MM-dd')] = {};
  }

  // 1. Urlaub & Krankheit vorab eintragen (Credits berechnen)
  const vacationHoursBase = 8 + (24 / 60);
  let totalCreditedVacationHours = 0;

  allDays.forEach(date => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const isDayWeekend = isWeekend(date);
    
    // Optimierung: Sick/Vacation Check hier zentral
    employees.forEach(emp => {
      // Sick Check
      if (emp.sickLeave?.from && emp.sickLeave?.to) {
        if (dateStr >= emp.sickLeave.from && dateStr <= emp.sickLeave.to) {
          tracker.dailyAssignments[emp.id][dateStr] = 'sick';
          return;
        }
      }
      // Vacation Check
      if (emp.vacation?.from && emp.vacation?.to) {
        if (dateStr >= emp.vacation.from && dateStr <= emp.vacation.to) {
          tracker.dailyAssignments[emp.id][dateStr] = 'vacation';
          if (!isDayWeekend) {
            const credit = vacationHoursBase * (emp.workload / 100);
            tracker.hours[emp.id] += credit;
            totalCreditedVacationHours += credit;
          }
        }
      }
    });
  });

  if (totalCreditedVacationHours > 0) {
    explanations.push({
      type: 'vacation_credit',
      message: 'Urlaubsstunden wurden angerechnet.'
    });
  }

  // ---------------------------------------------------------
  // CORE ASSIGNMENT LOGIC (Wiederverwendbar für WE und Werktage)
  // ---------------------------------------------------------
  const assignShift = (dateList, shift, requiredStaff, isWeekendBlock, contextOverride = null) => {
    const assignedIds = [];
    const firstDateStr = format(dateList[0], 'yyyy-MM-dd');
    const isHolidayBlock = dateList.some(d => {
      const dStr = format(d, 'yyyy-MM-dd');
      return settings.holidays?.some(h => h.date === dStr);
    });

    const context = contextOverride || (isHolidayBlock ? 'holiday' : (isWeekendBlock ? 'weekend' : 'normal'));

    // 1. Kandidaten filtern
    const candidates = employees.filter(emp => {
      // Bereits zugewiesen?
      if (dateList.some(d => tracker.dailyAssignments[emp.id][format(d, 'yyyy-MM-dd')])) return false;
      
      // Skill check
      if (!emp.skills.includes(shift)) return false;

      // Verfügbarkeit & Regeln für JEDEN Tag im Block prüfen
      for (const date of dateList) {
        const dStr = format(date, 'yyyy-MM-dd');
        const dayName = DAYS_OF_WEEK[getDay(date)];
        
        // Verfügbarkeit Wochentag
        if (!emp.availableDays[dayName]) return false;

        // Harte Präferenz-Sperre (z.B. "Frei"-Wunsch mit hoher Prio)
        const { penalty } = calculateScoreComponents(emp, dStr, shift, context);
        // Bei Critical (Unterbesetzung) sind wir toleranter (Threshold 40 statt 25)
        const threshold = context === 'critical_staffing' ? 40 : 25;
        if (penalty >= threshold) return false;

        // Max Consecutive Days Check (Lookback)
        const maxDays = Math.min(emp.maxConsecutiveDays || settings.rules.maxConsecutiveDays || 5, 5);
        // Wir prüfen, ob die Zuweisung des aktuellen Tages das Limit sprengen würde
        // (Achtung: Bei Blöcken > 1 Tag ist das eine Vereinfachung, reicht aber meist)
        const currentConsecutive = calculateConsecutiveDays(schedule, emp.id, dStr, dateMap);
        if (currentConsecutive >= maxDays) return false;

        // Ruhezeit (No Early after Late)
        if (shift === 'früh' && settings.rules.noEarlyAfterLate) {
          if (workedLateShiftYesterday(schedule, emp.id, dStr, dateMap)) return false;
        }
      }

      // Stunden-Limit Check (grob)
      const projectedHours = tracker.hours[emp.id] + (shiftDurations[shift] * dateList.length);
      const tolerance = settings.rules.hoursTolerance + (settings.rules.minutesTolerance / 60);
      if (projectedHours > tracker.targetHours[emp.id] + tolerance) return false;

      return true;
    });

    // 2. Scoring & Sortierung
    // Wir berechnen den Score einmal vor dem Sortieren für Performance
    const scoredCandidates = candidates.map(emp => {
      let totalPenalty = 0;
      let totalBonus = 0;
      let scoreDetails = [];

      dateList.forEach(date => {
        const dStr = format(date, 'yyyy-MM-dd');
        const comps = calculateScoreComponents(emp, dStr, shift, context);
        totalPenalty += comps.penalty;
        totalBonus += comps.bonus;
        scoreDetails.push(comps);
      });

      // Balance Score
      const stats = tracker.stats[emp.id];
      const ideal = tracker.idealDist[emp.id];
      const currentRatio = stats[shift] / Math.max(1, stats.totalDays);
      const idealRatio = ideal[shift] || 0;
      const balanceScore = idealRatio - currentRatio; // Positiv = unterrepräsentiert (gut)

      // Workload Score (Bevorzuge Leute mit hohem Pensum, die noch Stunden brauchen)
      const hoursProgress = tracker.hours[emp.id] / Math.max(1, tracker.targetHours[emp.id]);
      
      return {
        emp,
        totalPenalty,
        totalBonus,
        netScore: totalBonus - totalPenalty,
        balanceScore,
        hoursProgress,
        isBlocked: totalPenalty >= 50 // Sicherheitsnetz
      };
    });

    // Sortierung
    scoredCandidates.sort((a, b) => {
      // 1. Kritische Strafen vermeiden
      if (a.isBlocked !== b.isBlocked) return a.isBlocked ? 1 : -1;
      
      // 2. Net Preference Score (Höher ist besser)
      if (Math.abs(a.netScore - b.netScore) > 2) return b.netScore - a.netScore;
      
      // 3. Balance (Wer hat diese Schicht seltener gemacht?)
      if (Math.abs(a.balanceScore - b.balanceScore) > 0.1) return b.balanceScore - a.balanceScore;
      
      // 4. Hours Progress (Wer braucht noch Stunden? Niedriger Progress zuerst)
      return a.hoursProgress - b.hoursProgress;
    });

    // 3. Zuweisung
    for (let i = 0; i < Math.min(requiredStaff, scoredCandidates.length); i++) {
      const candidate = scoredCandidates[i];
      const emp = candidate.emp;
      
      assignedIds.push(emp.id);
      
      // Update State für jeden Tag im Block
      dateList.forEach(date => {
        const dStr = format(date, 'yyyy-MM-dd');
        
        if (!schedule[dStr][shift]) schedule[dStr][shift] = [];
        schedule[dStr][shift].push(emp.id);
        
        tracker.dailyAssignments[emp.id][dStr] = shift;
        tracker.hours[emp.id] += shiftDurations[shift];
        tracker.stats[emp.id][shift]++;
        tracker.stats[emp.id].totalDays++;
        
        // 🔥 FIXED: Only count weekend shifts once per weekend PAIR, not per day
        if (isWeekendBlock) {
          tracker.weekendShifts[emp.id] = (tracker.weekendShifts[emp.id] || 0) + 0.5; // 0.5 per day = 1 per weekend
        }
      });
    }

    return assignedIds;
  };

  // ---------------------------------------------------------
  // ABLAUF: WOCHENENDEN
  // ---------------------------------------------------------
  const weekendPairs = getWeekendPairs(allDays);
  
  for (const pair of weekendPairs) {
    // Check if Holiday
    const isHolidayBlock = pair.some(d => isHoliday(d, settings.holidays));
    const rules = isHolidayBlock ? settings.minStaffing.weekday : settings.minStaffing.weekend; // Feiertage wie Sonntag behandeln, aber Konfig beachten

    // Wir priorisieren Schichten für WE
    for (const shift of ['früh', 'spät', 'zwischen']) {
      const required = rules[shift] || 0;
      if (required === 0) continue;

      const assigned = assignShift(pair, shift, required, true, isHolidayBlock ? 'holiday' : 'weekend');

      // Check Understaffing
      if (assigned.length < required) {
        if (shift === 'zwischen') {
          // Zwischendienst droppen
          pair.forEach(d => daysWithoutZwischendienst.push(format(d, 'yyyy-MM-dd')));
          // Clean up partial assignments if logic dictates (here we keep partials)
        } else {
          pair.forEach(d => {
            violations.push({
              type: 'understaffed',
              date: format(d, 'yyyy-MM-dd'),
              shift,
              required,
              assigned: assigned.length
            });
          });
        }
      }
    }
  }

  // ---------------------------------------------------------
  // ABLAUF: WERKTAGE
  // ---------------------------------------------------------
  const weekDays = allDays.filter(d => !isWeekend(d));
  
  for (const date of weekDays) {
    const dateStr = format(date, 'yyyy-MM-dd');
    const isHolidayDate = isHoliday(date, settings.holidays);
    
    // Wenn Feiertag unter der Woche -> Nutzen wir Wochenend-Regeln (oder eigene Feiertagsregeln)
    const rules = isHolidayDate ? settings.minStaffing.weekend : settings.minStaffing.weekday;
    
    // Feiertags-Logik (Credits)
    if (isHolidayDate) {
      employees.forEach(emp => {
        // Wenn nicht arbeitet und nicht krank/urlaub -> Feiertagsstunden?
        // (Vereinfachung: Hier nur Credit wenn nicht eingeteilt, Logik kann komplexer sein)
        if (!tracker.dailyAssignments[emp.id][dateStr]) {
           const holHours = (8 + 24/60) * (emp.workload / 100);
           tracker.hours[emp.id] += holHours;
           // Markieren im Schedule als 'holiday' (frei)
           if (!schedule[dateStr]['holiday']) schedule[dateStr]['holiday'] = [];
           schedule[dateStr]['holiday'].push(emp.id);
           tracker.dailyAssignments[emp.id][dateStr] = 'holiday';
        }
      });
    }

    // Schichten zuweisen
    for (const shift of ['früh', 'spät', 'zwischen']) {
      const required = rules[shift] || 0;
      if (required === 0) continue;

      // Wenn Feiertag, dann ist Kontext 'holiday', sonst 'normal'
      // Wir überspringen, wenn der Tag schon durch WE-Logik (falls Feiertag) bearbeitet wurde?
      // Nein, weekDays filtert nur echte Wochenenden. Feiertage müssen hier behandelt werden.
      // ABER: Wenn Feiertag, dann versuchen wir zu besetzen.
      
      const assigned = assignShift([date], shift, required, false, isHolidayDate ? 'holiday' : 'normal');

      if (assigned.length < required) {
        if (shift === 'zwischen') {
          delete schedule[dateStr][shift]; // Falls leere Arrays existieren
          daysWithoutZwischendienst.push(dateStr);
        } else {
          violations.push({
            type: 'understaffed',
            date: dateStr,
            shift,
            required,
            assigned: assigned.length
          });
        }
      }
    }
  }

  if (daysWithoutZwischendienst.length > 0) {
    explanations.push({
      type: 'zwischendienst_omitted',
      message: `${daysWithoutZwischendienst.length} Tage ohne Zwischendienst zur Ressourcenschonung.`
    });
  }

  // Final Checks (Stundenbilanz)
  employees.forEach(emp => {
    const actual = tracker.hours[emp.id];
    const target = tracker.targetHours[emp.id];
    const diff = actual - target;
    const tolerance = settings.rules.hoursTolerance + (settings.rules.minutesTolerance / 60);
    
    if (diff > tolerance) {
      violations.push({
        type: 'hours_mismatch',
        employeeId: emp.id,
        employeeName: emp.name,
        target,
        actual,
        difference: diff,
        isOver: true
      });
    }
  });

  // 🔥 FIXED: Convert weekend shifts from half-count (0.5 per day) to whole weekends
  const correctedWeekendShifts = {};
  Object.entries(tracker.weekendShifts).forEach(([empId, count]) => {
    correctedWeekendShifts[empId] = Math.floor(count); // Round down to get whole weekends
  });

  return {
    schedule,
    violations,
    employeeHours: tracker.hours,
    employeeStats: tracker.stats,
    targetHours: tracker.targetHours,
    employeeWeekendShifts: correctedWeekendShifts, // 🔥 FIXED: Use corrected weekend counts
    explanations,
    daysWithoutZwischendienst,
    preferenceViolations: [], // (Vereinfacht, Details könnten aus assignShift kommen)
    preferenceSuccesses: [],
    preferenceScore: { totalScore: 0 },
    errors: []
  };
}